import { useState, useMemo, useEffect } from 'react';
import {
  LocalPostOffice,
  AttachEmail,
  MailOutline,
  PendingOutlined,
} from '@mui/icons-material';
import {
  ListTab,
  TabDrawerProps,
  AvatarsCell,
  TimestampCell,
  Loading,
  selectUser,
  ColorTypographyCell,
  RowCell,
} from '@centerpiece/components';
import {
  Button,
  IconButton,
  Typography,
  Chip,
  Badge,
  Paper,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
  Tooltip,
} from '@mui/material';
import { usePatchMessageContactMutation } from 'store/api';
import {
  Action,
  Coordination,
  Message,
  Reaction,
  Response,
  MessageContact,
} from 'store/api/models';
import { MessageTab } from './MessageTab';
import moment from 'moment';
import { ContactTooltip } from 'components/ContactTooltip';
import { useItemContext } from 'contexts/item.context';
import { useSelector } from 'react-redux';
import { AvailabilityTable } from 'components/AvailabilityTable';

export interface CoordinationTabProps
  extends Omit<TabDrawerProps, 'tabs' | 'setTabs' | 'title' | 'status'> {
  id?: number;
  transparentBackdrop?: boolean;
  createProject?: Function;
  afterSave?: Function;
  template?: boolean;
  disabled?: boolean;
  breadcrumbs?: { label: string; onClick?: Function }[];
  exampleCoordination?: Coordination;
}

export const CoordinationTab = (props: CoordinationTabProps) => {
  const {
    id,
    item,
    itemLoading,
    messages,
    task,
    event,
    refetchItem,
    setActiveTab,
  } = useItemContext();
  const { contact: userContact } = useSelector(selectUser);
  const [composing, setComposing] = useState<boolean>(
    !messages?.length || messages?.length === 0
  );
  const [message, setMessage] = useState<Message>();
  const [response, setResponse] = useState<Response>();

  useEffect(() => {
    if (!messages?.length || messages?.length === 0) {
      setComposing(true);
    } else {
      setComposing(false);
    }
  }, [messages]);

  const [patchMessageContact] = usePatchMessageContactMutation();
  const [open, setOpen] = useState<boolean>(false);
  const [searchKeyword, setSearchKeyword] = useState<string>('');

  const handleOpen = () => setOpen(true);

  const handleClose = () => setOpen(false);

  if (itemLoading) {
    return <Loading />;
  } else {
    return composing || message || response ? (
      <Paper sx={{ p: 2 }}>
        <MessageTab
          back={!!message || !!response || (composing && !!messages?.length)}
          message={message}
          response={response}
          onClose={() => {
            setComposing(false);
            setMessage(undefined);
            setResponse(undefined);
          }}
          onSubmit={(values: any) => {
            refetchItem();
            props?.afterSave && props?.afterSave();
          }}
        />
      </Paper>
    ) : (
      <>
        {messages?.map(m => {
          const { message, responses } = m;
          return (
            <AvailabilityTable
              {...m}
              checkboxes={false}
              title="Recipient Table"
              // setAppointment={setAppointment}
              onRowClick={async (row: any) => {
                const msg = row.msg;
                if (msg?.contacts?.[0]?.mc_id) {
                  await patchMessageContact({
                    id: msg?.contacts?.find((mc: any) => mc.id === userContact)
                      ?.mc_id,
                    status: 'Read',
                  } as MessageContact);
                  refetchItem();

                  props?.afterSave && props?.afterSave();
                }
                setResponse(msg);
              }}
              // onConfirmation={async (row: any) => {
              //   const msg = row.msg;
              //   setConfirmResponse(msg);
              // }}
              heading={
                <Typography
                  variant="body1"
                  sx={{
                    textAlign: 'left',
                    mb: 1,
                  }}
                >
                  <span style={{ fontWeight: 600 }}>Subject:</span>{' '}
                  <i>{message.subject}</i>{' '}
                  <small>
                    (
                    <u>
                      {m.message?.form?.canceled
                        ? 'Message Canceled'
                        : `${
                            [
                              ...new Set(
                                responses?.map(response => response.contact)
                              ),
                            ].length
                          } of ${
                            message?.contacts?.filter(c => !c.author)?.length
                          } Replies Received`}
                    </u>
                    )
                  </small>
                  <Button
                    sx={{ float: 'right', textDecoration: 'underline' }}
                    color={'secondary'}
                    onClick={() => setMessage(message)}
                  >
                    View or Edit Original Message
                  </Button>
                </Typography>
              }
              headCells={[
                {
                  name: 'visibility',
                  label: 'Visibility',
                  component: ColorTypographyCell,
                },
                {
                  name: 'name',
                  label: 'Name',
                },
                {
                  name: 'status',
                  label: 'Status',
                  component: ColorTypographyCell,
                },
                {
                  name: 'reactions',
                  label: '',
                  component: ColorTypographyCell,
                },
                {
                  name: 'msg',
                  disablePadding: false,
                  label: '',
                  align: 'right',
                  render: rowMsg => {
                    if (rowMsg.id === 0) {
                      return (
                        <Tooltip title="Response not yet received">
                          <IconButton>
                            <PendingOutlined />
                          </IconButton>
                        </Tooltip>
                      );
                    } else {
                      const msg = rowMsg.msg;
                      const attachments = [
                        ...(msg?.attachments || []),
                        ...(msg?.actions?.map(
                          (action: Action) => action.attachments
                        ) || []),
                        ...(msg?.reactions?.map(
                          (reaction: Reaction) => reaction.reactionattachments
                        ) || []),
                      ].flat(2);
                      const newMail =
                        msg?.contacts?.filter(
                          (contact: any) =>
                            contact.status === 'New' &&
                            contact.id === userContact
                        )?.length > 0;
                      return (
                        <Tooltip title="Click to view message">
                          <IconButton
                            onClick={async () => {
                              if (msg.msgType === 'message') {
                                setMessage(msg);
                              } else {
                                if (msg?.contacts?.[0]?.mc_id) {
                                  await patchMessageContact({
                                    id: msg?.contacts?.find(
                                      (mc: any) => mc.id === userContact
                                    )?.mc_id,
                                    status: 'Read',
                                  } as MessageContact);
                                  refetchItem();

                                  props?.afterSave && props?.afterSave();
                                }
                                setResponse(msg);
                              }
                            }}
                          >
                            {attachments.length > 0 ? (
                              <Badge
                                badgeContent={newMail ? 1 : 0}
                                variant="dot"
                                color={'secondary'}
                              >
                                <AttachEmail />
                              </Badge>
                            ) : (
                              <Badge
                                badgeContent={newMail ? 1 : 0}
                                variant="dot"
                                color={'secondary'}
                              >
                                <LocalPostOffice />
                              </Badge>
                            )}
                          </IconButton>
                        </Tooltip>
                      );
                    }
                  },
                },
              ]}
              rows={[
                ...responses?.map(
                  (response: Response) =>
                    (({
                      id: response.id,
                      name: !!response?.contact_r?.firstname
                        ? `${response?.contact_r.firstname} ${response?.contact_r.lastname}`
                        : response?.contact_r.email,
                      visibility: {
                        text: (response?.contact_r as any)?.mc?.message_type,
                      },
                      status: {
                        text: 'Replied',
                        subtext: `(${moment(response.created_at).format(
                          'MM/DD/YYYY'
                        )})`,
                        color: 'secondary',
                      },
                      reactions: {
                        text: (
                          <span>
                            {response?.reactions
                              .filter(r => r?.status)
                              .map((reaction, index) => (
                                <Chip
                                  key={index}
                                  color={getChipColorFromStatus(
                                    reaction?.status
                                  )}
                                  label={reaction?.status}
                                  variant="filled"
                                />
                              ))}
                          </span>
                        ),
                      },
                      msg: response,
                    } as RowCell) || ([] as RowCell[]))
                ),
                ...message.contacts
                  .filter(
                    c =>
                      !c.author && !responses.find(r => r.contact_r.id === c.id)
                  )
                  .map(c => ({
                    id: 0,
                    name: !!c.firstname
                      ? `${c.firstname} ${c.lastname}`
                      : c.email,
                    status: {
                      text: c.status === 'New' ? 'Not Read' : c.status,
                      subtext:
                        c.status !== 'New' &&
                        `(${moment((c as any).mc_updated_at).format(
                          'MM/DD/YYYY'
                        )})`,
                    },
                    visibility: { text: c.message_type },
                  })),
              ]}
            />
          );
        })}
        {false &&
          messages?.map(m => (
            <Paper elevation={3} sx={{ p: 2, mb: 5 }}>
              <ListTab
                disableFilter={true}
                summary={
                  <Typography
                    variant="h6"
                    sx={{
                      textAlign: 'center',
                      textDecoration: 'underline',
                    }}
                  >
                    {m.message?.form?.canceled
                      ? 'MESSAGE CANCELED'
                      : `${
                          [
                            ...new Set(
                              m.responses?.map(response => response.contact)
                            ),
                          ].length
                        } of ${
                          (m.message?.contacts?.length || 1) - 1
                        } Replies Received`}
                  </Typography>
                }
                table={{
                  rows: [
                    {
                      ...m.message,
                      listsubject: (
                        <span>
                          <b>{`${m.message.contact_r?.firstname} ${m.message.contact_r?.lastname} sent: `}</b>
                          {m.message.subject}
                        </span>
                      ),
                      recipients: {
                        data: m.message.contacts.map(mc => ({
                          ...mc,
                          role: mc.role_r,
                        })),
                        tooltip: ContactTooltip,
                      },
                      created_at: { timestamp: m.message.created_at },
                      msgType: 'message',
                      read: true,
                      tooltip: ContactTooltip, //message.contacts.find(c => c.status === 'New')
                    },
                    ...(m.responses
                      ? m.responses?.map((response: Response) => ({
                          ...response,
                          listsubject:
                            response?.reactions?.filter(r => r.status).length >
                            0 ? (
                              <span>
                                <b>
                                  {`${
                                    !!response?.contact_r?.firstname
                                      ? `${response?.contact_r.firstname} ${response?.contact_r.lastname}`
                                      : response?.contact_r.email
                                  } replied: `}{' '}
                                  {response?.reactions
                                    ?.filter(r =>
                                      [
                                        'approval_request',
                                        'readiness_request',
                                      ].includes(r.form?.type)
                                    )
                                    .map((reaction, index) => (
                                      <Chip
                                        key={index}
                                        color={getChipColorFromStatus(
                                          reaction?.status
                                        )}
                                        label={reaction?.status}
                                        variant="filled"
                                      />
                                    ))}
                                </b>
                              </span>
                            ) : (
                              <span>
                                <b>{`${
                                  !!response?.contact_r?.firstname
                                    ? `${response?.contact_r.firstname} ${response?.contact_r.lastname}`
                                    : response?.contact_r.email
                                } replied`}</b>
                              </span>
                            ),
                          recipients: {
                            data: [response.contact_r],
                            tooltip: ContactTooltip,
                          },
                          created_at: { timestamp: response.created_at },
                          msgType: 'reply',
                          read:
                            !response.contacts ||
                            response.contacts.length === 0 ||
                            !!response.contacts.find(c => c.status === 'Read'),
                        }))
                      : []),
                  ].sort(
                    (a: any, b: any) => -moment(a.created_at).diff(b.created_at)
                  ),
                  headCells: [
                    {
                      name: 'recipients',
                      disablePadding: false,
                      label: 'Recipient',
                      component: AvatarsCell,
                      render: (row, name) => ({
                        ...row[name],
                        avatarGroupProps: {
                          style: { paddingTop: 16, width: 'fit-content' },
                        },
                      }),
                    },
                    {
                      name: 'created_at',
                      disablePadding: false,
                      label: 'Date & Time',
                      component: TimestampCell,
                      render: (row, name) => ({
                        ...row[name],
                        sx: { py: 0.5 },
                      }),
                    },
                    {
                      name: 'listsubject',
                      disablePadding: false,
                      label: 'Subject',
                      // component: StatusTextCell,
                    },
                    // {
                    //   name: 'status',
                    //   disablePadding: false,
                    //   label: 'Status',
                    //   component: StatusTextCell,
                    // },
                    {
                      name: 'id',
                      disablePadding: false,
                      label: 'Message',
                      render: msg => {
                        const attachments = [
                          ...(msg?.attachments || []),
                          ...(msg?.actions?.map(
                            (action: Action) => action.attachments
                          ) || []),
                          ...(msg?.reactions?.map(
                            (reaction: Reaction) => reaction.reactionattachments
                          ) || []),
                        ].flat(2);
                        const mc_id = msg?.contacts?.find(
                          (mc: any) =>
                            mc.id === userContact &&
                            !mc.author &&
                            mc.status === 'New'
                        )?.mc_id;

                        return (
                          <IconButton
                            onClick={async () => {
                              if (mc_id) {
                                await patchMessageContact({
                                  id: mc_id,
                                  status: 'Read',
                                } as MessageContact);
                                refetchItem();
                                props?.afterSave && props?.afterSave();
                              }
                              if (msg.msgType === 'message') {
                                setMessage(msg);
                              } else {
                                setResponse(msg);
                              }
                            }}
                          >
                            {attachments.length > 0 ? (
                              <Badge
                                badgeContent={attachments.length}
                                color={'secondary'}
                              >
                                <AttachEmail />
                              </Badge>
                            ) : (
                              <Badge
                                badgeContent={mc_id ? 1 : 0}
                                variant="dot"
                                color={'secondary'}
                              >
                                <LocalPostOffice />
                              </Badge>
                            )}
                          </IconButton>
                        );
                      },
                    },
                    {
                      name: 'child',
                      disablePadding: false,
                      label: 'Message',
                      align: 'right',
                      render: ({ id, name }) => (
                        <Button
                          variant="contained"
                          color="error"
                          onClick={() => {}}
                          sx={{
                            fontSize: 12,
                            textTransform: 'none',
                            height: 30,
                            marginRight: 1,
                          }}
                        >
                          Send {name} a message
                        </Button>
                      ),
                    },
                  ],
                }}
                // actions={[
                //   {
                //     name: 'Add Message',
                //     icon: <MailOutline />,
                //     onClick: () => setComposing(true),
                //   },
                // ]}
              />
            </Paper>
          ))}
        <SpeedDial
          ariaLabel=""
          sx={{
            position: 'fixed',
            right: 0,
            bottom: 56,
            /**
             * Width should be drawer width
             */
            width: {
              xs: '100%',
              md: '50%',
            },
          }}
          icon={<SpeedDialIcon />}
          onClose={handleClose}
          onOpen={handleOpen}
          open={open}
        >
          <SpeedDialAction
            key={'Add Message'}
            icon={<MailOutline />}
            tooltipTitle={'Add Message'}
            tooltipOpen
            onClick={() => {
              setComposing(true);
              handleClose();
            }}
          />
        </SpeedDial>
      </>
    );
  }
};

const getChipColorFromStatus = (status: string) => {
  switch (status) {
    case 'Approved':
    case 'Approved with Markups':
    case 'approved':
    case 'Confirmed':
    case 'confirmed':
      return 'secondary';
    case 'Call Me':
    case 'call-me':
      return 'warning';
    case 'Rejected':
    case 'rejected':
    case 'Not Ready':
    case 'not-ready':
      return 'error';
    default:
      return 'default';
  }
};
